import React, { useState, useEffect, useRef } from 'react';
import { SmallDropdown } from '../../../../components/shareable/dropdownMenu';
import { SmallInputField } from '../../../../components/shareable/input';
import { useDashboard } from '../../../../context/dashboardContext';
import CreateSaw from '../../saw/components/createSaw';
import CosenLibraryModal from './CosenLibraryModal';
import PdfEditorModal from './PdfEditorModal';
import toast from 'react-hot-toast';

// Sentinel option shown at the top of the Saw Brand & Model dropdown. Picking it opens the
// same "Add New Saw" modal used on the Saws management screen. Exported so the standalone
// update page can reuse the exact same label.
export const ADD_NEW_SAW_OPTION = '➕ Add New Saw';

const SawConveyorDetails = ({ dropdownOptions, selectedConveyorStyle, handleConveyorStyleSelection, getCurrentRollerLengths, selectedRollerLength, handleRollerLengthSelect, sawOptions, handleSawSelection, handleRemoveSaw, handleAddNewSaw, formFields, selectedSaw, setSelectedSaw, quoteBrochure = '', setQuoteBrochure }) => {
  const { getCosenFile, getBrochureFile, uploadQuoteBrochure } = useDashboard();
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [brochureFile, setBrochureFile] = useState(null);
  const [newFilename, setNewFilename] = useState(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [editedPrice, setEditedPrice] = useState('');
  const [showAddSaw, setShowAddSaw] = useState(false);
  // Brochure source flow: choose Computer vs Cosen Library, then (for Library, or when
  // opening the saved brochure) a PDF editor the user can annotate. The brochure is saved
  // PER QUOTE (quoteBrochure) — never written back to the saw or the Cosen original.
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [showCosenLibrary, setShowCosenLibrary] = useState(false);
  const [editorState, setEditorState] = useState({ open: false, bytes: null, name: '', base: '' });
  const [savingBrochure, setSavingBrochure] = useState(false);
  const [openingBrochure, setOpeningBrochure] = useState(false);
  const fileInputRef = useRef(null);

  const sawItems = handleAddNewSaw ? [ADD_NEW_SAW_OPTION, ...sawOptions] : sawOptions;
  const onSawDropdownSelect = (value) => {
    if (value === ADD_NEW_SAW_OPTION) setShowAddSaw(true);
    else handleSawSelection(value);
  };

  useEffect(() => {
    // Reset transient computer-upload state when the saw changes. The per-quote brochure
    // itself is independent of the saw, so it is NOT reset here.
    setUploadError(null);
    setBrochureFile(null);
    setNewFilename(null);
    setEditedPrice(selectedSaw?.price || formFields.price || '');
  }, [selectedSaw, formFields.price]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setBrochureFile(file);
      
      setUploadError(null);
      const originalName = file.name;
      const originalNameWithoutExt = originalName.replace(/\.[^/.]+$/, '');
      const timestamp = Date.now();
      const generatedFilename = `brochure_${safeNamePart(originalNameWithoutExt)}_${timestamp}.pdf`;
      setNewFilename(generatedFilename);
    }
  };

  // Keep generated S3 keys clean: collapse anything that isn't a safe filename char
  // (spaces, parentheses, etc.) to underscores so brochure URLs need no escaping.
  const safeNamePart = (s) => (s || '').replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '') || 'brochure';

  // Base name (no extension/timestamp) for generated filenames. If the current brochure is
  // already a generated "brochure_<base>_<ts>.pdf", reuse <base> so re-edits stay tidy.
  const brochureBaseName = (name) => {
    const noExt = (name || '').replace(/\.pdf$/i, '');
    const m = noExt.match(/^brochure_(.*)_\d+$/);
    return m ? m[1] : noExt;
  };

  // Computer upload (step 2): store the chosen PDF on THIS QUOTE (not the saw).
  const handleUploadBrochure = async () => {
    if (!brochureFile || !newFilename) return;
    setUploadLoading(true);
    setUploadError(null);
    try {
      const result = await uploadQuoteBrochure(brochureFile, newFilename);
      if (result.success) {
        setBrochureFile(null);
        setNewFilename(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setQuoteBrochure(result.filename);
        toast.success('Brochure added to this quote!');
      } else {
        setUploadError(result.error || 'Failed to upload brochure');
        toast.error(result.error || 'Failed to upload brochure');
      }
    } catch (error) {
      console.error('Error uploading brochure:', error);
      setUploadError('An error occurred while uploading the brochure');
      toast.error('An error occurred while uploading the brochure');
    } finally {
      setUploadLoading(false);
    }
  };

  // Opening the brochure picker: if this quote already has one, confirm replacement first;
  // otherwise go straight to the source-choice modal (Computer vs Cosen Library).
  const handleChooseFileClick = () => {
    if (quoteBrochure) setShowReplaceModal(true);
    else setShowSourceModal(true);
  };

  const handleReplaceConfirm = () => {
    setShowReplaceModal(false);
    setShowSourceModal(true);
  };

  // Source choice: upload from the computer (2-step flow via the hidden input).
  const handleSourceComputer = () => {
    setShowSourceModal(false);
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // Source choice: pick from the Cosen library.
  const handleSourceLibrary = () => {
    setShowSourceModal(false);
    setShowCosenLibrary(true);
  };

  // A library PDF was chosen: fetch its bytes and open the editor.
  const handleCosenSelect = async (item) => {
    const res = await getCosenFile(item.key);
    if (!res.success) {
      toast.error(res.error || 'Could not load that PDF');
      return;
    }
    setShowCosenLibrary(false);
    setEditorState({ open: true, bytes: res.data, name: item.name, base: brochureBaseName(item.name) });
  };

  // Click the saved brochure filename: load this quote's current brochure into the editor
  // so it can be viewed and edited again.
  const handleViewBrochure = async () => {
    if (!quoteBrochure) return;
    setOpeningBrochure(true);
    try {
      const res = await getBrochureFile(quoteBrochure);
      if (!res.success || !res.data) {
        toast.error(res.error || 'Could not open the brochure');
        return;
      }
      setEditorState({ open: true, bytes: res.data, name: quoteBrochure, base: brochureBaseName(quoteBrochure) });
    } catch (err) {
      console.error('Error opening brochure:', err);
      toast.error('Could not open the brochure');
    } finally {
      setOpeningBrochure(false);
    }
  };

  // Remove this quote's brochure (does not delete the S3 object; just detaches it).
  const handleRemoveBrochure = () => {
    if (setQuoteBrochure) setQuoteBrochure('');
  };

  // Editor "Use this brochure": upload the (possibly edited) PDF and store its filename on
  // THIS QUOTE. A fresh timestamped name is generated each save, so editing never mutates
  // the saw's brochure, another quote's brochure, or the Cosen library original.
  const handleEditorSave = async (editedBytes) => {
    setSavingBrochure(true);
    try {
      const generated = `brochure_${safeNamePart(editorState.base)}_${Date.now()}.pdf`;
      const file = new File([editedBytes], generated, { type: 'application/pdf' });
      const result = await uploadQuoteBrochure(file, generated);
      if (result.success) {
        setBrochureFile(null);
        setNewFilename(null);
        setQuoteBrochure(result.filename);
        setEditorState({ open: false, bytes: null, name: '', base: '' });
        toast.success('Brochure saved to this quote!');
      } else {
        toast.error(result.error || 'Failed to save brochure');
      }
    } catch (err) {
      console.error('Error saving brochure:', err);
      toast.error('An error occurred while saving the brochure');
    } finally {
      setSavingBrochure(false);
    }
  };

  // New handler for price changes
  const handlePriceChange = (e) => {
    const newPrice = e.target.value;
    setEditedPrice(newPrice);
    
    // Update selectedSaw with the new price for the current quote
    if (selectedSaw) {
      setSelectedSaw({
        ...selectedSaw,
        price: newPrice
      });
    }
  };

  return (
    <div>
      <h2 className="text-xs font-medium text-gray-600 mb-1">Saw & Conveyor Details</h2>
      <div className="bg-white p-3 rounded shadow">
        <div className="flex flex-wrap gap-3 overflow-x-auto pb-2">
          <SmallDropdown label="Conveyor Style" items={dropdownOptions?.conveyorStyles?.map((s) => s.label)} selected={dropdownOptions.conveyorStyles?.find((s) => s.value === selectedConveyorStyle)?.label || ''} onSelect={(v) => handleConveyorStyleSelection(dropdownOptions.conveyorStyles?.find((s) => s.label === v)?.value || '')} className="text-sm w-full sm:w-40 flex-shrink-0" />
          <SmallDropdown label="Roller Length" items={getCurrentRollerLengths()} selected={selectedRollerLength} onSelect={(v) => handleRollerLengthSelect(v)} disabled={!selectedConveyorStyle} className="text-sm w-full sm:w-28 flex-shrink-0" />
          <div className="flex items-end gap-1 w-full sm:w-auto flex-shrink-0">
            <SmallDropdown label="Saw Brand & Model" items={sawItems} selected={selectedSaw ? `${selectedSaw.brand || 'Unknown Brand'} - ${selectedSaw.model || 'Unknown Model'}` : ''} onSelect={onSawDropdownSelect} className="text-sm w-full sm:w-52" />
            {selectedSaw && handleRemoveSaw && (
              <button
                type="button"
                onClick={handleRemoveSaw}
                title="Remove saw"
                className="mb-[1px] inline-flex items-center justify-center h-[26px] px-2 text-[11px] font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-1 focus:ring-red-400 flex-shrink-0"
              >
                Remove
              </button>
            )}
          </div>
          <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
            <SmallInputField label="Datum Side" value={formFields.datumSide} readOnly className="text-sm w-full sm:w-28" />
            <SmallInputField label="Work Bed Height" value={formFields.workBedHeight} readOnly className="text-sm w-full sm:w-36" />
          </div>
          <SmallInputField label="Saw Type" value={formFields.sawType} readOnly className="text-sm w-full sm:w-52 flex-shrink-0" />
          <SmallInputField label="Saw Price" value={editedPrice} onChange={handlePriceChange} disabled={!selectedSaw} className="text-sm w-full sm:w-52 flex-shrink-0" />
        </div>

        <div className="grid grid-cols-10 gap-2 -mt-2">
          <div className="col-span-3 mt-1">
            <div className="-mb-0.5"><label className="mb-0.5 text-[15px] font-medium underline text-gray-600 block">Brochure</label></div>
            <div className="h-6 flex items-center justify-center mt-1">
              {selectedSaw ? (
                <>
                  <input type="file" accept=".pdf" onChange={handleFileChange} ref={fileInputRef} className="hidden" id="brochure-upload" />
                  {!brochureFile ? (
                    <>
                      {quoteBrochure ? (
                        <div className="flex items-center w-full gap-1">
                          {/* Click the filename to view/edit this quote's brochure */}
                          <button type="button" onClick={handleViewBrochure} disabled={openingBrochure} title="Click to view / edit" className="text-xs truncate flex-1 text-left text-red-700 underline hover:text-red-900 disabled:opacity-50">
                            {openingBrochure ? 'Opening…' : quoteBrochure}
                          </button>
                          <button onClick={handleChooseFileClick} title="Replace brochure" className="inline-flex items-center justify-center h-6 px-2 text-[11px] border border-transparent font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-1 focus:ring-red-400">Replace</button>
                          <button onClick={handleRemoveBrochure} title="Remove brochure" className="inline-flex items-center justify-center h-6 px-2 text-[11px] border border-transparent font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-400">Remove</button>
                        </div>
                      ) : (
                        <button type="button" onClick={handleChooseFileClick} className="inline-flex items-center justify-center h-6 w-full px-2 text-[11px] border border-transparent font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-1 focus:ring-offset-0 focus:ring-red-400 text-center cursor-pointer">Choose File To Upload</button>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center w-full">
                      <span className="text-xs truncate flex-1">
                        <span className="font-medium text-gray-700">File Name:</span> {newFilename}
                      </span>
                      <button onClick={handleUploadBrochure} disabled={uploadLoading} className="ml-1 inline-flex items-center justify-center h-6 w-[100px] px-2 text-[11px] border border-transparent font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-1 focus:ring-offset-0 focus:ring-red-400 disabled:opacity-50">
                        {uploadLoading ? (<><svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Uploading...</>) : ('Upload')}
                      </button>
                    </div>
                  )}
                </>
              ) : (<span className="text-gray-500 text-[11px] block">Select Saw First</span>)}
            </div>
            {uploadError && <div className="text-red-500 text-[10px] mt-0.5 text-center">{uploadError}</div>}
          </div>
          <div className="col-span-7">
            <label className="mb-0.5 text-[15px] font-medium underline text-gray-600">Description</label>
            <textarea className="w-full px-1.5 py-0.5 text-[11px] border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-400 bg-gray-50" value={selectedSaw?.description || ''} readOnly rows={1} />
          </div>
        </div>
      </div>

      {handleAddNewSaw && (
        <CreateSaw Update="Add New Saw" isOpen={showAddSaw} onClose={() => setShowAddSaw(false)} onSave={handleAddNewSaw} />
      )}

      {/* Source choice: upload from computer or pick from the Cosen library */}
      {showSourceModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="relative p-5 border w-[22rem] shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 text-center">Add Brochure</h3>
            <p className="text-sm text-gray-500 text-center mt-1">Where is the PDF coming from?</p>
            <div className="flex flex-col gap-3 mt-5">
              <button type="button" onClick={handleSourceComputer} className="w-full px-4 py-3 bg-red-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-red-700">Select from Computer</button>
              <button type="button" onClick={handleSourceLibrary} className="w-full px-4 py-3 bg-gray-100 text-gray-800 text-sm font-medium rounded-md shadow-sm hover:bg-gray-200 border border-gray-300">Select from Cosen Library</button>
              <button type="button" onClick={() => setShowSourceModal(false)} className="w-full px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <CosenLibraryModal isOpen={showCosenLibrary} onClose={() => setShowCosenLibrary(false)} onSelect={handleCosenSelect} />

      <PdfEditorModal
        isOpen={editorState.open}
        fileBytes={editorState.bytes}
        fileName={editorState.name}
        saving={savingBrochure}
        onCancel={() => setEditorState({ open: false, bytes: null, name: '', base: '' })}
        onSave={handleEditorSave}
      />

      {showReplaceModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative p-4 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Replace Brochure</h3>
              <div className="mt-2 px-7 py-3"><p className="text-sm text-gray-500">A brochure already exists for this saw. Do you want to replace it with a new file?</p></div>
              <div className="flex justify-center items-center gap-4 px-4 py-3">
                <button onClick={handleReplaceConfirm} className="w-40 px-4 py-2 bg-red-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300">Select New File</button>
                <button onClick={() => setShowReplaceModal(false)} className="w-40 px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SawConveyorDetails;
